import { type Key, useState } from 'react';
import { BiTrash } from 'react-icons/bi';
import { BsX } from 'react-icons/bs';
import { iconClasses } from '@/config/job';
import { ActionDropdown, Button, Confirm, cn } from '@/core/ui';

type ReviewMultiAction = {
  key: string;
  title: string;
  operation: 'delete';
  type: 'success' | 'danger' | 'warn' | 'info' | 'default';
  icon: React.ComponentType<{ className?: string }>;
  color?: 'default' | 'danger' | 'warning' | 'secondary';
};

const REVIEW_MULTI_ACTIONS: ReviewMultiAction[] = [
  {
    key: 'delete',
    title: 'Delete',
    operation: 'delete',
    type: 'danger',
    icon: BiTrash,
    color: 'danger',
  },
  // {
  //   key: 'setTodo',
  //   title: 'Set state: Todo',
  //   operation: 'setState',
  //   type: 'default',
  //   icon: MdOutlinePendingActions,
  //   stateValue: 1,
  // },
  // {
  //   key: 'setReview',
  //   title: 'Set state: Review',
  //   operation: 'setState',
  //   type: 'default',
  //   icon: MdOutlinePendingActions,
  //   stateValue: 2,
  // },
  // {
  //   key: 'setDone',
  //   title: 'Set state: Done',
  //   operation: 'setState',
  //   type: 'success',
  //   icon: MdOutlineDoneOutline,
  //   stateValue: 3,
  // },
];

export const ReviewMultiActions = ({
  selectedReviews,
  onDelete,
  isLoading,
  onClearSelection,
}: {
  selectedReviews: (string | number)[];
  onDelete: (ids: (string | number)[]) => Promise<void> | void;
  isLoading: boolean;
  onClearSelection: () => void;
}) => {
  const [selectedAction, setSelectedAction] = useState<ReviewMultiAction | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  if (!selectedReviews.length) {
    return null;
  }

  const actionItems = REVIEW_MULTI_ACTIONS.map((action) => ({
    key: action.key,
    className: 'text-xs py-1',
    startContent: (
      <action.icon
        className={cn(iconClasses, {
          'text-danger-300': action.color === 'danger',
          'text-warning-500': action.color === 'warning',
          'text-secondary-500': action.color === 'secondary',
        })}
      />
    ),
    title: action.title,
    color: action.color,
  }));

  const handleAction = (key: Key) => {
    const action = REVIEW_MULTI_ACTIONS.find((a) => a.key === key);
    if (action) {
      setSelectedAction(action);
      setIsConfirmOpen(true);
    }
  };

  const handleConfirm = async () => {
    if (!selectedAction) return;
    await onDelete(selectedReviews);
    setIsConfirmOpen(false);
    setSelectedAction(null);
  };

  const getDescription = (action: ReviewMultiAction, count: number) => {
    return `Are you sure you want to ${action.title.toLowerCase()} ${count} ${
      count === 1 ? 'review' : 'reviews'
    }?`;
  };

  return (
    <>
      <div className='flex items-center gap-2'>
        <Button
          size='sm'
          variant='flat'
          className='flex items-center px-0'
          onPress={onClearSelection}
          aria-label='Clear selected reviews'
        >
          <span className='text-xs flex items-center justify-center text-default-900 rounded-md'>
            {selectedReviews.length}
          </span>
          <BsX className='size-4' />
        </Button>

        <ActionDropdown
          offset={0}
          classNames={{
            content: 'shadow-sm border border-default-200 rounded-md min-w-[160px]',
          }}
          onAction={handleAction}
          items={actionItems}
        >
          <Button size='sm' variant='flat' color='default'>
            Actions
          </Button>
        </ActionDropdown>
      </div>

      {selectedAction && (
        <Confirm
          type={selectedAction.type}
          title={`${selectedAction.title} Reviews`}
          isLoading={isLoading}
          isOpen={isConfirmOpen}
          onOpenChange={setIsConfirmOpen}
          message={
            <div className='flex flex-col gap-2'>
              <p>{getDescription(selectedAction, selectedReviews.length)}</p>
              <div className='bg-warning-50 border border-warning-200 rounded p-2 mt-2'>
                <p className='text-xs text-warning-700 font-semibold mb-1'>Selected Review IDs:</p>
                <ul className='list-disc list-inside text-xs text-warning-800 max-h-32 overflow-y-auto'>
                  {selectedReviews.map((id) => (
                    <li key={id}>{id}</li>
                  ))}
                </ul>
              </div>
            </div>
          }
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
